import React, { useState } from 'react';
import { Pagination, PaginationItem, PaginationLink, Table } from 'reactstrap';
import {
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    useReactTable,
} from '@tanstack/react-table';

const BasicTable = ({ data, columns, customPageSize, filtering, setFiltering }) => {
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: customPageSize });

    const table = useReactTable({
        data,
        columns,
        state: { sorting, pagination, globalFilter: filtering },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        onGlobalFilterChange: setFiltering,
    });

    const totalPages = table.getPageCount();
    const currentPage = table.getState().pagination.pageIndex;

    return (
        <>
            <div className="scroll-y-table">
                <Table className="table-bordered table-nowrap align-middle mb-0">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th key={header.id} onClick={header.column.getToggleSortingHandler()}>
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        {header.column.getCanSort() && (
                                            header.column.getIsSorted() ? (
                                                <i
                                                    className={`ri-arrow-${header.column.getIsSorted() === 'asc' ? 'up' : 'down'
                                                        }-line fw-light ms-2`}
                                                ></i>
                                            ) : (
                                                <i className="ri-arrow-up-down-line fw-light ms-2"></i>
                                            )
                                        )}
                                    </th>

                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.length > 0 ? (
                            table.getRowModel().rows.map((row) => (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <div className="mt-2 d-flex justify-content-between align-items-center">
                <div className="text-muted fs-12">
                    Showing <strong>{table.getState().pagination.pageIndex * customPageSize + 1}</strong>-
                    <strong>{Math.min((table.getState().pagination.pageIndex + 1) * customPageSize, data.length)}</strong> of{' '}
                    <strong>{data.length}</strong> Results
                </div>

                <Pagination size="sm" className="pagination-sm">
                    <PaginationItem disabled={!table.getCanPreviousPage()}>
                        <PaginationLink onClick={() => table.setPageIndex(0)}>1</PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={!table.getCanPreviousPage()}>
                        <PaginationLink onClick={table.previousPage}>← Prev</PaginationLink>
                    </PaginationItem>
                    {Array.from({ length: totalPages }).map((_, index) => {
                        if (index === 0 || index === totalPages - 1 ||
                            (index >= currentPage - 2 && index <= currentPage + 2)) {
                            return (
                                <PaginationItem key={index} active={index === currentPage}>
                                    <PaginationLink onClick={() => table.setPageIndex(index)}>
                                        {index + 1}
                                    </PaginationLink>
                                </PaginationItem>
                            );
                        }
                        if (index === currentPage - 3 || index === currentPage + 3) {
                            return (
                                <PaginationItem key={index} disabled>
                                    <PaginationLink>...</PaginationLink>
                                </PaginationItem>
                            );
                        }
                        return null;
                    })}
                    <PaginationItem disabled={!table.getCanNextPage()}>
                        <PaginationLink onClick={table.nextPage}>Next →</PaginationLink>
                    </PaginationItem>
                    <PaginationItem disabled={!table.getCanNextPage()}>
                        <PaginationLink onClick={() => table.setPageIndex(totalPages - 1)}>
                            {totalPages}
                        </PaginationLink>
                    </PaginationItem>
                </Pagination>
            </div>
        </>
    );
};

export default BasicTable;